import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { LottieSpinner } from "@/components/ui/LottieSpinner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useRouteRoleSync } from "@/hooks/core/useRouteRoleSync";

// Route modules
import { adminRoutes } from "./admin.routes";
import { playerRoutes } from "./player.routes";
import { tournamentRoutes } from "./tournament.routes";
import { clubRoutes } from "./club.routes";
import { coachRoutes } from "./coach.routes";
import { organizationRoutes } from "./organization.routes";
import { onboardingRoutes } from "./onboarding.routes";

// Common pages
const Index = lazy(() => import("@/pages/Index"));
// Le ternaire n'est pas décoratif : sans lui, l'import dynamique subsiste et
// Rollup émet le chunk du banc dans le build de production, où il est mort.
// `import.meta.env.DEV` étant remplacé par `false`, la branche est éliminée.
const ScoreDesignCheck = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/ScoreDesignCheck"))
  : null;
const CreateMatchDesignCheck = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/CreateMatchDesignCheck"))
  : null;
const MatchDetailDesignCheck = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/MatchDetailDesignCheck"))
  : null;
const MatchCardsDesignCheck = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/MatchCardsDesignCheck"))
  : null;
const CompetitionCardsDesignCheck = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/CompetitionCardsDesignCheck"))
  : null;
const LaunchDesignCheck = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/LaunchDesignCheck"))
  : null;
const OrganizerDesignCheck = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/OrganizerDesignCheck"))
  : null;
const PlayerDesignCheck = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/PlayerDesignCheck"))
  : null;
const NavigationDesignCheck = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/NavigationDesignCheck"))
  : null;
const PlanningDesignCheck = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/PlanningDesignCheck"))
  : null;
const EventDesignCheck = import.meta.env.DEV
  ? lazy(() => import("@/pages/dev/EventDesignCheck"))
  : null;
const StyleGuide = import.meta.env.DEV ? lazy(() => import("@/pages/dev/StyleGuide")) : null;
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const SetupPage = lazy(() => import("@/pages/SetupPage"));
const JoinOrganization = lazy(() => import("@/pages/JoinOrganization"));
const RequestRole = lazy(() => import("@/pages/RequestRole"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const InviteJoinPage = lazy(() => import("@/pages/InviteJoinPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const PaymentConfirmation = lazy(() => import("@/pages/payment/PaymentConfirmation"));
const TermsPage = lazy(() => import("@/pages/legal/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/legal/PrivacyPage"));
const SecurityPage = lazy(() => import("@/pages/legal/SecurityPage"));
const VoucherVerifyPage = lazy(() => import("@/pages/voucher/VoucherVerifyPage"));
const Settings = lazy(() => import("@/pages/player/Settings"));
const OrganizationDetailPlayer = lazy(() => import("@/pages/player/OrganizationDetailPlayer"));
const PublicPlayerProfile = lazy(() => import("@/pages/public/PublicPlayerProfile"));
const PublicEventPage = lazy(() => import("@/pages/public/PublicEventPage"));
const CompetitionShareRedirect = lazy(() => import("@/pages/public/CompetitionShareRedirect"));

/** Redirect legacy /casual-matches/:id to /player/matches/:id */
function RedirectCasualMatch() {
  const { id } = useParams();
  return <Navigate to={`/player/matches/${id}`} replace />;
}

/**
 * Main application routes
 */
export function AppRoutes() {
  useRouteRoleSync();

  return (
    <Suspense fallback={<LottieSpinner />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
        <Route path="/auth" element={<AuthPage />} />
        {/* Banc de comparaison visuelle — développement uniquement, absent du
            build de production. Voir src/pages/dev/ScoreDesignCheck.tsx. */}
        {ScoreDesignCheck && (
          <Route path="/dev/score" element={<ScoreDesignCheck />} />
        )}
        {CreateMatchDesignCheck && (
          <Route path="/dev/create-match" element={<CreateMatchDesignCheck />} />
        )}
        {MatchDetailDesignCheck && (
          <Route path="/dev/match-detail" element={<MatchDetailDesignCheck />} />
        )}
        {MatchCardsDesignCheck && (
          <Route path="/dev/match-cards" element={<MatchCardsDesignCheck />} />
        )}
        {CompetitionCardsDesignCheck && (
          <Route path="/dev/competition-cards" element={<CompetitionCardsDesignCheck />} />
        )}
        {LaunchDesignCheck && (
          <Route path="/dev/lancement" element={<LaunchDesignCheck />} />
        )}
        {OrganizerDesignCheck && (
          <Route path="/dev/organisateur/*" element={<OrganizerDesignCheck />} />
        )}
        {/* Le `/*` est obligatoire : sans lui, le <Routes> imbrique du banc ne matche jamais. */}
        {PlayerDesignCheck && (
          <Route path="/dev/joueur/*" element={<PlayerDesignCheck />} />
        )}
        {NavigationDesignCheck && (
          <Route path="/dev/navigation" element={<NavigationDesignCheck />} />
        )}
        {PlanningDesignCheck && (
          <Route path="/dev/planning" element={<PlanningDesignCheck />} />
        )}
        {EventDesignCheck && <Route path="/dev/evenement/*" element={<EventDesignCheck />} />}
        {StyleGuide && <Route path="/dev/style-guide" element={<StyleGuide />} />}
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/join-organization" element={<JoinOrganization />} />
        <Route path="/invite/:code" element={<InviteJoinPage />} />
        <Route path="/request-role" element={<RequestRole />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/v/:voucherCode" element={<VoucherVerifyPage />} />
        <Route path="/players/:id" element={<PublicPlayerProfile />} />
        {/* Fiche d'événement partageable, sans compte. Open Graph : api/event-og.js. */}
        <Route path="/e/:slug" element={<PublicEventPage />} />
        {/* Liens partagés d'une compétition (« Communiquer »). Open Graph : api/competition-og.js. */}
        <Route path="/t/:slug" element={<CompetitionShareRedirect kind="tournament" />} />
        <Route path="/c/:slug" element={<CompetitionShareRedirect kind="championship" />} />
        {/*
          Un seul composant vivait à deux adresses, dont une HORS de la coquille joueur :
          ouvrir les réglages depuis un lien profond faisait disparaître la navigation.
          `/player/settings` fait foi.
        */}
        <Route path="/settings" element={<Navigate to="/player/settings" replace />} />
        <Route path="/organizations/:id" element={<ProtectedRoute><OrganizationDetailPlayer /></ProtectedRoute>} />

        {/* Legacy redirects — old notification links stored in DB */}
        <Route path="/casual-matches/:id" element={<RedirectCasualMatch />} />
        <Route path="/player/court-bookings" element={<Navigate to="/player/find-court" replace />} />
        <Route path="/player/bookings" element={<Navigate to="/player/find-court" replace />} />
        <Route path="/player/payments" element={<Navigate to="/player/credits" replace />} />
        <Route path="/player/payment-update" element={<Navigate to="/player/credits" replace />} />
        <Route path="/player/events" element={<Navigate to="/player/agenda" replace />} />
        <Route path="/tournaments/create" element={<Navigate to="/tournament-manager/create" replace />} />
        <Route path="/association/members" element={<Navigate to="/" replace />} />
        <Route path="/dashboard" element={<Navigate to="/" replace />} />

        {/* Feature-based routes */}
        {adminRoutes}
        {playerRoutes}
        {tournamentRoutes}
        {clubRoutes}
        {coachRoutes}
{organizationRoutes}

        {/* Onboarding */}
        {onboardingRoutes}

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
